import { useState, useEffect } from 'react';
import { addDoc, collection, Timestamp, updateDoc, doc, query, getDocs, orderBy, limit } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase-config';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { X, Loader2, CheckCircle2, Upload, AlertCircle, ChevronUp } from 'lucide-react';

interface SubmitRequestModalProps {
  customerId: string;
  customerName: string;
  brandColor: string;
  onClose: () => void;
  onSuccess: () => void;
}

const requestSchema = z.object({
  title: z.string().min(5, 'Title must be at least 5 characters'),
  description: z.string().min(20, 'Description must be at least 20 characters'),
  category: z.enum(['feature', 'improvement', 'bug_fix', 'other']),
});

type RequestFormData = z.infer<typeof requestSchema>;

interface SimilarRequest {
  id: string;
  title: string;
  voteCount: number;
  status: string;
}

export default function SubmitRequestModal({
  customerId,
  customerName,
  brandColor,
  onClose,
  onSuccess,
}: SubmitRequestModalProps) {
  const { user, userProfile } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [imagePreviewUrls, setImagePreviewUrls] = useState<string[]>([]);
  const [similarRequests, setSimilarRequests] = useState<SimilarRequest[]>([]);
  const [searchingSimilar, setSearchingSimilar] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<RequestFormData>({
    resolver: zodResolver(requestSchema),
    defaultValues: {
      title: '',
      description: '',
      category: 'feature',
    },
  });

  const titleValue = watch('title');

  // Search for similar requests as user types
  useEffect(() => {
    const searchSimilar = async () => {
      if (!titleValue || titleValue.length < 5) {
        setSimilarRequests([]);
        return;
      }

      setSearchingSimilar(true);
      try {
        const requestsRef = collection(db, 'customers', customerId, 'requests');
        const q = query(requestsRef, orderBy('voteCount', 'desc'), limit(50));
        const snapshot = await getDocs(q);

        const allRequests = snapshot.docs.map(doc => ({
          id: doc.id,
          title: doc.data().title,
          voteCount: doc.data().voteCount,
          status: doc.data().status,
        })) as SimilarRequest[];

        // Simple similarity check - if title contains similar words
        const searchTerms = titleValue.toLowerCase().split(' ').filter(term => term.length > 2);
        const similar = allRequests.filter(req => {
          const reqTitle = req.title.toLowerCase();
          return searchTerms.some(term => reqTitle.includes(term));
        }).slice(0, 3); // Show top 3 similar requests

        setSimilarRequests(similar);
      } catch (err) {
        console.error('Error searching similar requests:', err);
      } finally {
        setSearchingSimilar(false);
      }
    };

    // Debounce the search
    const timer = setTimeout(searchSimilar, 500);
    return () => clearTimeout(timer);
  }, [titleValue, customerId]);

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const fileArray = Array.from(files);

    // Validate: max 3 images, max 5MB each
    if (selectedImages.length + fileArray.length > 3) {
      setError('Maximum 3 images allowed');
      return;
    }

    for (const file of fileArray) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Each image must be less than 5MB');
        return;
      }
      if (!file.type.startsWith('image/')) {
        setError('Only image files are allowed');
        return;
      }
    }

    setError(null);
    setSelectedImages(prev => [...prev, ...fileArray]);

    // Generate preview URLs
    fileArray.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviewUrls(prev => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index: number) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviewUrls(prev => prev.filter((_, i) => i !== index));
  };

  const uploadImages = async (requestId: string): Promise<string[]> => {
    const uploadPromises = selectedImages.map(async (file, index) => {
      const storageRef = ref(
        storage,
        `customers/${customerId}/requests/${requestId}/${Date.now()}_${index}_${file.name}`
      );
      await uploadBytes(storageRef, file);
      return getDownloadURL(storageRef);
    });

    return Promise.all(uploadPromises);
  };

  const onSubmit = async (data: RequestFormData) => {
    if (!user || !userProfile) {
      setError('You must be logged in to submit a request');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Create request document first
      const requestDoc = await addDoc(collection(db, 'customers', customerId, 'requests'), {
        title: data.title,
        description: data.description,
        category: data.category,
        customerId: customerId,
        createdBy: user.uid,
        createdByEmail: user.email,
        createdByNickname: userProfile.nickname,
        status: 'open',
        voteCount: 1, // Creator automatically votes
        imageUrls: [], // Will be updated if images are uploaded
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });

      // Upload images if any
      if (selectedImages.length > 0) {
        const imageUrls = await uploadImages(requestDoc.id);
        // Update request with image URLs
        await updateDoc(doc(db, 'customers', customerId, 'requests', requestDoc.id), {
          imageUrls: imageUrls,
        });
      }

      // Create initial vote from creator
      await addDoc(
        collection(db, 'customers', customerId, 'requests', requestDoc.id, 'votes'),
        {
          userId: user.uid,
          userEmail: user.email || '',
          userNickname: userProfile.nickname || '',
          customerId: customerId,
          requestId: requestDoc.id,
          createdAt: Timestamp.now(),
        }
      );

      setSuccess(true);
      reset();
      setSelectedImages([]);
      setImagePreviewUrls([]);

      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1500);
    } catch (err) {
      console.error('Error submitting request:', err);
      setError('Failed to submit request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // If not authenticated, show login prompt
  if (!user) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-card border border-border rounded-lg max-w-md w-full m-4 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-foreground">
              Sign in to submit
            </h2>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <p className="text-muted-foreground mb-6">
            You need to be signed in to submit feature requests.
          </p>
          <div className="space-y-3">
            <Button
              className="w-full"
              style={{ backgroundColor: brandColor }}
              onClick={() => (window.location.href = '/login')}
            >
              Sign In
            </Button>
            <Button variant="outline" className="w-full" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-card border border-border rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto m-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-2xl font-semibold text-foreground">
              Submit Feature Request
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Request a feature or improvement for {customerName}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
          {/* Success Message */}
          {success && (
            <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
              <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                <CheckCircle2 className="h-5 w-5" />
                <span className="font-medium">Request submitted successfully!</span>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">
              Title <span className="text-destructive">*</span>
            </Label>
            <Input
              id="title"
              {...register('title')}
              placeholder="Brief description of your request"
              className={errors.title ? 'border-destructive' : ''}
              disabled={isSubmitting || success}
            />
            {errors.title && (
              <p className="text-sm text-destructive">{errors.title.message}</p>
            )}
          </div>

          {/* Similar Requests Warning */}
          {similarRequests.length > 0 && (
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-amber-900 dark:text-amber-100 mb-2">
                    Similar requests already exist
                  </h4>
                  <p className="text-sm text-amber-800 dark:text-amber-200 mb-3">
                    Before submitting, check if your idea already exists. You can vote on existing requests instead!
                  </p>
                  <div className="space-y-2">
                    {similarRequests.map((req) => (
                      <div
                        key={req.id}
                        className="bg-white dark:bg-gray-800 border border-amber-200 rounded-md p-3 flex items-center justify-between gap-3"
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="flex items-center gap-1 text-sm font-medium" style={{ color: brandColor }}>
                            <ChevronUp className="h-4 w-4" />
                            <span>{req.voteCount}</span>
                          </div>
                          <span className="text-sm text-foreground truncate">{req.title}</span>
                        </div>
                        <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600 whitespace-nowrap">
                          {req.status.replace('_', ' ')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {searchingSimilar && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Checking for similar requests...</span>
            </div>
          )}

          {/* Category */}
          <div className="space-y-2">
            <Label htmlFor="category">
              Category <span className="text-destructive">*</span>
            </Label>
            <select
              id="category"
              {...register('category')}
              className="w-full h-10 px-3 rounded-md border border-input bg-background text-foreground"
              disabled={isSubmitting || success}
            >
              <option value="feature">New Feature</option>
              <option value="improvement">Improvement</option>
              <option value="bug_fix">Bug Fix</option>
              <option value="other">Other</option>
            </select>
            {errors.category && (
              <p className="text-sm text-destructive">{errors.category.message}</p>
            )}
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">
              Description <span className="text-destructive">*</span>
            </Label>
            <textarea
              id="description"
              {...register('description')}
              rows={6}
              placeholder="Provide details about your request. What problem does it solve? How would it work?"
              className={`w-full px-3 py-2 rounded-md border bg-background text-foreground resize-none ${
                errors.description ? 'border-destructive' : 'border-input'
              }`}
              disabled={isSubmitting || success}
            />
            {errors.description && (
              <p className="text-sm text-destructive">{errors.description.message}</p>
            )}
          </div>

          {/* Image Upload */}
          <div className="space-y-2">
            <Label htmlFor="images">
              Attachments (Optional)
            </Label>
            <div className="space-y-3">
              {/* Image previews */}
              {imagePreviewUrls.length > 0 && (
                <div className="grid grid-cols-3 gap-3">
                  {imagePreviewUrls.map((url, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={url}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-24 object-cover rounded border border-border"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        disabled={isSubmitting}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Upload button */}
              {selectedImages.length < 3 && (
                <div>
                  <input
                    id="images"
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageSelect}
                    disabled={isSubmitting || success}
                    className="hidden"
                  />
                  <label htmlFor="images">
                    <div
                      className="border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:bg-muted/50 transition-colors"
                    >
                      <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                      <p className="text-sm text-foreground font-medium">
                        Click to upload images
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Up to 3 images, max 5MB each
                      </p>
                    </div>
                  </label>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || success}
              style={{ backgroundColor: brandColor }}
              className="text-white"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : success ? (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Submitted!
                </>
              ) : (
                'Submit Request'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
